import React, { useState, useEffect } from 'react';
import { permissionService } from '../api/services';
import AdminModal from './AdminModal';
import './UserPermissionsModal.css';

// 後台「使用者管理」用：替個別使用者「加掛」任意多個功能權限（與角色已授予者取聯集）。
// 角色已授予的權限以唯讀勾選呈現（標示「角色已授予」），個人加掛的權限可勾選後儲存。
// ADMIN 目標為隱性全權，全部顯示為已勾選且不可調整。
const UserPermissionsModal = ({ user, onClose, onSaved }) => {
  const [catalog, setCatalog] = useState([]);
  const [direct, setDirect] = useState(new Set());
  const [roleGranted, setRoleGranted] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    let cancelled = false;
    Promise.all([permissionService.catalog(), permissionService.getUserPermissions(user.id)])
      .then(([catRes, userRes]) => {
        if (cancelled) return;
        setCatalog(catRes.data || []);
        setDirect(new Set(userRes.data?.direct || []));
        setRoleGranted(new Set(userRes.data?.roleGranted || []));
      })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user.id]);

  const toggle = (key) => {
    setDirect((prev) => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await permissionService.setUserPermissions(user.id, [...direct]);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setSaving(false);
    }
  };

  const displayName = user?.nickname || user?.name || user?.account;

  return (
    <AdminModal title={`指派個人權限：${displayName}`} onClose={onClose}>
      {error && <div className="alert alert-danger">{error}</div>}
      {isAdmin ? (
        <div className="perm-admin-note">此使用者為管理員（ADMIN），擁有全部權限，無需且不可個別指派。</div>
      ) : loading ? (
        <div className="perm-loading">載入中...</div>
      ) : (
        <>
          <p className="perm-hint">勾選要「個別加掛」給這位使用者的權限。標示「角色已授予」者由其角色帶入，無法在此移除（需至角色矩陣調整）。</p>
          <div className="perm-list">
            {catalog.map((p) => {
              const fromRole = roleGranted.has(p.key);
              const checked = fromRole || direct.has(p.key);
              return (
                <label key={p.key} className={`perm-row ${fromRole ? 'from-role' : ''}`}>
                  <input type="checkbox" checked={checked} disabled={fromRole} onChange={() => toggle(p.key)} />
                  <span className="perm-text">
                    <span className="perm-label">{p.label} <code>{p.key}</code></span>
                    {p.description && <span className="perm-desc">{p.description}</span>}
                  </span>
                  {fromRole && <span className="perm-tag">角色已授予</span>}
                </label>
              );
            })}
            {catalog.length === 0 && <div className="perm-empty">目前沒有任何權限可指派</div>}
          </div>
          <div className="btn-group" style={{ justifyContent: 'flex-end', marginTop: 16 }}>
            <button className="btn btn-sm btn-primary" disabled={saving} onClick={handleSave}>{saving ? '儲存中...' : '儲存'}</button>
            <button className="btn btn-sm btn-secondary" disabled={saving} onClick={onClose}>取消</button>
          </div>
        </>
      )}
    </AdminModal>
  );
};

export default UserPermissionsModal;
